<template>
    <div>
        <div class="toolbar-compensation"></div>
        <v-toolbar class="content-toolbar">
            <v-btn flat class="back-button" v-on:click="$router.go(-1)">
                <font-awesome-icon :icon="['far', 'chevron-left']" size="2x"/>
            </v-btn>
            <v-toolbar-title>Users</v-toolbar-title>
            <span v-if="!currentUser" class="right-icon">
                <font-awesome-icon :icon="['fal', 'spinner-third']" spin size="2x"/>
            </span>
            <v-spacer></v-spacer>
            <div v-if="isExportStateLoadedSuccessfully">
                <v-btn :href="exportState.data.url" target="_blank">
                    Download
                </v-btn>
            </div>
            <div v-else>
                <v-btn :loading="isExportStateLoading" @click="onExportButtonClick">
                    <span class="export-text">Export</span>
                    <font-awesome-icon :icon="['fal', 'download']" size="2x"/>
                </v-btn>
            </div>

        </v-toolbar>

        <div class="container">
            <v-data-table v-if="currentUser"
                          :headers="headers"
                          :items="users.data"
                          :pagination.sync="paginationSync"
                          :loading="users.state === LOADING_STARTED"
                          class="elevation-1 hidden-rows-per-page-selection">
                <v-progress-linear slot="progress" :indeterminate="true" color="accent"></v-progress-linear>
                <template slot="items" slot-scope="props">
                    <td>{{ props.item.username }}</td>
                    <td>{{ props.item.fullName }}</td>
                    <td>{{ props.item.emailAddress }}</td>
                    <td>
                        <span>{{ props.item.role }}</span>
                    </td>
                    <td>
                        <v-menu offset-y :disabled="isSelf(props.item)">
                            <v-btn slot="activator" small color="accent" :disabled="isSelf(props.item)">
                                <span>Change Role</span>
                            </v-btn>
                            <v-list>
                                <v-list-tile v-for="role in possibleRoles.filter(filterCurrentRoleFactory(props.item))"
                                             :key="role"
                                             v-on:click="confirmUserRoleUpgrade(props.item, role)">
                                    <v-list-tile-title>
                                        <div>{{role}}</div>
                                    </v-list-tile-title>
                                </v-list-tile>
                            </v-list>
                        </v-menu>
                        <v-btn small fab color="error" @click="confirmDeleteUser(props.item)">
                            <font-awesome-icon :icon="['fas', 'minus']"/>
                        </v-btn>
                    </td>

                </template>
            </v-data-table>
            <div class="new-key-dialog-container">
                <v-dialog v-model="addUserDialogState" max-width="500px" persistent>
                    <v-btn slot="activator" class="add-key-button" color="green" dark fab>
                        <span><font-awesome-icon :icon="['fal', 'plus']"/></span>
                    </v-btn>
                    <v-card>
                        <v-form v-model="addUserVFormModel" :ref="addUserFormRefName"
                                @submit.prevent="handleAddUserSubmit">
                            <v-card-title>
                                <span class="headline">New User</span>
                            </v-card-title>
                            <v-card-text>
                                <v-container grid-list-md>
                                    <v-layout wrap>
                                        <v-flex>
                                            <v-text-field v-model="newUser.data.firstName" label="First name"
                                                          autofocus
                                                          color="accent"></v-text-field>
                                            <v-text-field v-model="newUser.data.lastName" label="Last name"
                                                          color="accent"></v-text-field>
                                            <v-text-field v-model="newUser.data.emailAddress" label="Email"
                                                          :rules="addUserFormEmailRules"
                                                          color="accent"></v-text-field>
                                            <v-text-field v-model="newUser.data.username" label="Username"
                                                          :rules="addUserFormUsernameValidationRules"
                                                          color="accent"></v-text-field>
                                            <v-text-field v-model="newUser.data.password" label="Password"
                                                          :rules="addUserFormPasswordRules" type="password"
                                                          color="accent"></v-text-field>
                                            <v-select
                                                    :items="possibleRoles"
                                                    color="accent"
                                                    v-model="newUser.data.role"
                                                    label="Role"
                                            ></v-select>
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn color="blue darken-1" flat @click.native="closeAddUserDialog">Cancel</v-btn>
                                <v-btn type="submit" color="blue darken-1" flat :loading="isAddUserLoading"
                                       :disabled="isAddUserFormSubmitButtonDisabled">
                                    {{addUserButtonText}}
                                </v-btn>
                            </v-card-actions>
                        </v-form>
                    </v-card>
                </v-dialog>
            </div>
            <v-dialog v-model="upgradeUserRoleDialogState" max-width="500px">
                <v-card>
                    <v-card-title>
                        <span class="headline">Change Role</span>
                    </v-card-title>
                    <v-card-text>
                        <p v-if="userToUpgrade.data">
                            Continuing will update {{userToUpgrade.data.username}} to a {{userToUpgrade.data.role}}
                            role.
                            <br><br>
                            If you would like to abort, click cancel. Otherwise, click continue.
                        </p>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" flat @click.native="closeUpdateDialog">Cancel</v-btn>
                        <v-btn color="blue darken-1" flat @click.native="saveUser">Update</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
            <v-dialog v-model="deleteUserRoleDialogState" max-width="500px">
                <v-card>
                    <v-card-title>
                        <span class="headline">Delete User</span>
                    </v-card-title>
                    <v-card-text>
                        <p v-if="userToDelete.data">
                            Are you sure you would like to delete
                            <strong>{{userToDelete.data.username}}</strong> permanently?
                        </p>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn flat @click="closeDeleteDialog" :disabled="isDeleteUserLoading">Cancel</v-btn>
                        <v-btn color="error" flat @click="deleteUser" :loading="isDeleteUserLoading"
                               :disabled="isDeleteUserLoading">Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>

<script>
    import {
        LOADING_PRISTINE,
        LOADING_STARTED,
        LOADING_COMPLETED_SUCCESS,
        LOADING_COMPLETED_FAILURE,
        SUPER_ADMIN_ROLE,
        ADMIN_ROLE,
        USER_ROLE,
        LOAD_EXPORT_USERS_URL,
    } from "../services/constants"
    import BaseLoadingComponent from './BaseLoading'
    import http from '../services/axios'
    import lodash from '../services/lodash'

    const userConstructor = () => {
        return {
            state: LOADING_PRISTINE,
            data: null,
            err: null,
        }
    }

    const ADD_NEW_USER_FORM_REF_NAME = 'add-user-form'
    export default {
        name: 'settings-users',
        extends: BaseLoadingComponent,
        data: function () {
            return {
                dialog: false,
                addUserDialogState: false,
                upgradeUserRoleDialogState: false,
                deleteUserRoleDialogState: false,
                possibleRoles: [SUPER_ADMIN_ROLE, ADMIN_ROLE, USER_ROLE],
                userToUpgrade: userConstructor(),
                userToDelete: userConstructor(),
                users:
                    {
                        state: LOADING_PRISTINE,
                        data: [],
                        err: null,
                    }
                ,
                newUser: userConstructor(),
                addUserModelTemplate: {
                    firstName: '',
                    lastName: '',
                    username: '',
                    emailAddress: '',
                    password: '',
                    role: USER_ROLE,
                },
                paginationSync: {
                    rowsPerPage: 25,
                },
                addUserVFormModel: false
            }
        },
        computed: {
            headers() {
                return [
                    {text: 'Username', value: 'username'},
                    {text: 'Full name', value: 'fullName'},
                    {text: 'Email', value: 'emailAddress'},
                    {text: 'Role', value: 'role'},
                    {text: 'Actions', value: 'name', sortable: false}
                ]
            },
            spaceMetaData: function () {
                return this.$store.state.spaceMetadata
            },
            currentUser: function () {
                return this.$store.state.currentUser
            },
            isAddUserLoading() {
                return this.newUser.state === LOADING_STARTED
            },
            addUserButtonText() {
                return this.newUser.state === LOADING_COMPLETED_FAILURE ? 'Try Again' : 'Add'
            },
            isAddUserFormValid() {
                const formModel = this.newUser.data
                return !!formModel.emailAddress && !!formModel.username && !!formModel.password
            },
            isAddUserFormSubmitButtonDisabled() {
                return !this.isAddUserFormValid
            },
            addUserFormEmailRules() {
                return [
                    (model) => {
                        if (!model) {
                            return 'Email is required'
                        }
                        if (model.length < 3) {
                            return 'Email is too short'
                        }
                        if (!model.includes('@')) {
                            return 'Email missing @ symbol'
                        }
                        if (!model.includes('.')) {
                            return 'Email not formatted correctly'
                        }
                        return true
                    },
                ]
            },
            addUserFormUsernameValidationRules() {
                return [
                    (model) => {
                        if (!model) {
                            return 'Username is required'
                        }
                        if (model.length < 3) {
                            return 'Username is too short'
                        }
                        return true
                    },
                ]
            },
            addUserFormPasswordRules() {
                return [
                    (model) => {
                        if (!model) {
                            return 'Password is required'
                        }
                        if (model.length < 6) {
                            return 'Password is too short'
                        }
                        return true
                    },
                ]
            },
            addUserFormRefName() {
                return ADD_NEW_USER_FORM_REF_NAME
            },
            exportState() {
                return this.$store.state.usersModule.exportUrlState
            },
            isExportStateLoading() {
                return this.exportState.state === LOADING_STARTED
            },
            isExportStateLoadedSuccessfully() {
                return this.exportState.state === LOADING_COMPLETED_SUCCESS
            },
            isDeleteUserLoading() {
                return this.userToDelete.state === LOADING_STARTED
            }
        },
        watch: {
            spaceMetaData: function () {
                this.init()
            },
        },
        methods: {
            getUsers() {
                this.users = {
                    state: LOADING_STARTED,
                    data: [],
                    err: null,
                }

                return http.get(`api/management/spaces/${this.$store.state.spaceMetadata.id}/users`)
                    .then(response => {
                        console.log(response.data)
                        const mappedResult = response.data.map(user => {
                            let fullName = ''
                            if (user.firstName) {
                                fullName += user.firstName
                            }
                            if (user.lastName) {
                                fullName += ' '
                                fullName += user.lastName
                            }
                            user['fullName'] = fullName
                            return user
                        })
                        this.users = {
                            state: LOADING_COMPLETED_SUCCESS,
                            data: mappedResult,
                            err: null,
                        }
                    })
                    .catch(err => {
                        this.users = {
                            state: LOADING_COMPLETED_FAILURE,
                            data: [],
                            err: err,
                        }
                    })
            },
            updateUserRole: function () {
                http.post(`api/management/spaces/${this.$store.state.spaceMetadata.id}/users/${this.userToUpgrade.data.id}/roles/${this.userToUpgrade.data.role}`)
                    .then(response => {
                        const newUserState = userConstructor()
                        newUserState.state = LOADING_COMPLETED_SUCCESS
                        this.userToUpgrade = newUserState
                        this.getUsers()
                    })
                    .catch(err => {
                        const newUserState = userConstructor()
                        newUserState.state = LOADING_COMPLETED_FAILURE
                        newUserState.err = err
                        this.userToUpgrade = newUserState
                    })
            },
            addNewUser: function () {
                this.newUser.state = LOADING_STARTED
                return http.post(`api/spaces/${this.$store.state.spaceMetadata.id}/users`, this.newUser.data)
                    .then(response => {
                        return http.post(`api/management/spaces/${this.$store.state.spaceMetadata.id}/users/${response.data.id}/roles/${this.newUser.data.role}`)
                            .then(response => {
                                this.newUser = {
                                    state: LOADING_COMPLETED_SUCCESS,
                                    data: lodash.cloneDeep(this.addUserModelTemplate),
                                    err: null,
                                }
                                this.getUsers()
                            })
                    })
                    .catch(err => {
                        this.newUser = {
                            state: LOADING_COMPLETED_FAILURE,
                            data: this.newUser.data,
                            err: err,
                        }
                    })
            },
            async deleteUser() {
                this.userToDelete.state = LOADING_STARTED
                try {
                    await http.delete(`api/management/spaces/${this.$store.state.spaceMetadata.id}/users/${this.userToDelete.data.id}`)
                    const newUserState = userConstructor()
                    newUserState.state = LOADING_COMPLETED_SUCCESS
                    this.userToDelete = newUserState
                    await this.getUsers()

                } catch (e) {
                    const newUserState = userConstructor()
                    newUserState.state = LOADING_COMPLETED_FAILURE
                    newUserState.err = e
                    this.userToDelete = newUserState
                }
                this.deleteUserRoleDialogState = false
            },
            init: function () {
                this.newUser = {
                    state: LOADING_PRISTINE,
                    data: lodash.cloneDeep(this.addUserModelTemplate),
                    err: null,
                }
                if (this.spaceMetaData) {
                    this.getUsers()
                }
            },
            isSelf(user) {
                return user.id === this.currentUser.id
            },
            closeUpdateDialog() {
                this.upgradeUserRoleDialogState = false
            },
            saveUser() {
                this.updateUserRole()
                this.closeUpdateDialog()
            },
            confirmUserRoleUpgrade(user, newRole) {
                this.userToUpgrade.data = lodash.cloneDeep(user)
                this.userToUpgrade.data.role = newRole
                this.upgradeUserRoleDialogState = true
            },
            confirmDeleteUser(user) {
                this.userToDelete = userConstructor()
                this.userToDelete.data = user
                this.deleteUserRoleDialogState = true
            },
            closeDeleteDialog() {
                this.deleteUserRoleDialogState = false
            },
            closeAddUserDialog() {
                this.addUserDialogState = false
                this.$refs[ADD_NEW_USER_FORM_REF_NAME].resetValidation()
            },
            addUser() {
                this.addNewUser().then(() => {
                    if (this.newUser.state === LOADING_COMPLETED_SUCCESS) {
                        this.closeAddUserDialog()
                    }
                })
            },
            filterCurrentRoleFactory(user) {
                return function (role) {
                    return role !== user.role
                }
            },
            handleAddUserSubmit() {
                if (this.isAddUserFormValid) {
                    this.addNewUser().then(() => {
                        if (this.newUser.state === LOADING_COMPLETED_SUCCESS) {
                            this.closeAddUserDialog()
                        }
                    })
                }
            },
            onExportButtonClick() {
                this.$store.dispatch(LOAD_EXPORT_USERS_URL)
            }
        },
        created: function () {
            this.init()
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    .toolbar-compensation {
        height: 64px;
    }

    .back-button {
        height: 100%;
        margin: 0 1em 0 0;
    }

    .key-column-container {
        width: 20em;
    }

    .new-key-dialog-container {
        display: flex;
        justify-content: flex-end;
    }

    .add-key-button {
        margin: 1em 0 0 0;
    }

    .right-icon {
        margin: 0 0 0 1em;
    }

    .export-text {
        margin: 0 1em 0 0;
    }
</style>
